import { supabase } from "@/integrations/supabase/client";
import { Challenge, ChallengeListItem, ChallengeType } from "@/types/challenge";
import { buildDuplicatePayload, DuplicateSourceConfig } from "./challenge-duplicate";

export interface CreateChallengeInput {
  title: string;
  description: string;
  challenge_type: ChallengeType;
  start_date: string;
  end_date: string;
  total_days: number;
  status?: string;
  cover_image?: string;
  preparation_days?: number;
  purchase_reference?: string;
  pace_mode?: string;
  pace_days?: number[];
  anchor_day?: number;
  grace_period_days?: number;
  entry_line?: string;
  paid_entry?: boolean;
}

export interface UpdateChallengeInput {
  title?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  total_days?: number;
  cover_image?: string;
  preparation_days?: number;
}

export const challengeService = {
  // יצירת אתגר חדש (admin only)
  async createChallenge(challenge: CreateChallengeInput): Promise<any> {
    const { data, error } = await supabase
      .from("challenges")
      .insert(challenge as any)
      .select()
      .single();

    if (error) throw error;
    
    const challengeId = data.id;
    const preparationDays = challenge.preparation_days || 0;
    const totalDays = challenge.total_days;
    
    // יצירת ימי הכנה (ימים שליליים: -1, -2, -3...)
    for (let i = 1; i <= preparationDays; i++) {
      await supabase.from("challenge_days").insert({
        challenge_id: challengeId,
        day_number: -i,
        video_id: `prep-video-${i}`,
        video_url: "",
        video_title: `יום הכנה ${i}`,
        video_description: "",
      });
    }
    
    // יצירת ימי אתגר רגילים (1 עד totalDays)
    for (let i = 1; i <= totalDays; i++) {
      await supabase.from("challenge_days").insert({
        challenge_id: challengeId,
        day_number: i,
        video_id: `video-${i}`,
        video_url: "",
        video_title: `יום ${i}`,
        video_description: "",
      });
    }
    
    // המרה מ-snake_case ל-camelCase
    return {
      ...data,
      startDate: data.start_date,
      endDate: data.end_date,
      totalDays: data.total_days,
      coverImage: data.cover_image,
      challengeType: data.challenge_type,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      participantsCount: data.participants_count,
    };
  },

  // עדכון אתגר קיים (admin only)
  async updateChallenge(id: string, updates: UpdateChallengeInput): Promise<any> {
    // בדיקה: אם יש משתתפים, חסום שינויים מבניים
    const structuralFields: (keyof UpdateChallengeInput)[] = ["start_date", "total_days", "preparation_days"];
    const hasStructuralChanges = structuralFields.some(field => updates[field] !== undefined);

    if (hasStructuralChanges) {
      const { data: challenge } = await supabase
        .from("challenges")
        .select("participants_count")
        .eq("id", id)
        .single();

      if (challenge && challenge.participants_count > 0) {
        throw new Error("לא ניתן לשנות שדות מבניים באתגר עם משתתפים פעילים");
      }
    }

    const { data, error } = await supabase
      .from("challenges")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    
    // המרה מ-snake_case ל-camelCase
    return {
      ...data,
      startDate: data.start_date,
      endDate: data.end_date,
      totalDays: data.total_days,
      coverImage: data.cover_image,
      challengeType: data.challenge_type,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      participantsCount: data.participants_count,
    };
  },

  // בדיקה אם יש משתתפים פעילים באתגר
  async getChallengeParticipantsCount(id: string): Promise<number> {
    const { data, error } = await supabase
      .from("challenges")
      .select("participants_count")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data?.participants_count ?? 0;
  },

  // מחיקת אתגר (admin only) — חסום אם יש משתתפים פעילים
  async deleteChallenge(id: string): Promise<void> {
    const participantsCount = await this.getChallengeParticipantsCount(id);
    if (participantsCount > 0) {
      throw new Error(`לא ניתן למחוק אתגר עם ${participantsCount} משתתפים פעילים. יש להסיר את כל המשתתפים לפני המחיקה.`);
    }

    const { error } = await supabase
      .from("challenges")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  // קבלת כל האתגרים
  async getAllChallenges(): Promise<ChallengeListItem[]> {
    const { data, error } = await supabase
      .from("challenges")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw error;
    
    // המרה מ-snake_case ל-camelCase
    return data.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      status: item.status as "active" | "upcoming" | "completed",
      startDate: item.start_date,
      endDate: item.end_date,
      totalDays: item.total_days,
      coverImage: item.cover_image,
      participantsCount: item.participants_count,
      challengeType: item.challenge_type as ChallengeType,
      paceMode: (item as any).pace_mode as 'daily' | 'calendar_days' | 'self_paced' | undefined,
      entryLine: (item as any).entry_line ?? undefined,
      paidEntry: (item as any).paid_entry ?? undefined,
    }));
  },

  // קבלת אתגר לפי ID
  async getChallengeById(id: string): Promise<Challenge | null> {
    const { data, error } = await supabase
      .from("challenges")
      .select(`
        *,
        days:challenge_days(
          *,
          tasks:challenge_day_tasks(*),
          features:challenge_day_features(*)
        )
      `)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      throw error;
    }
    
    if (!data) return null;

    // המרה למבנה של Challenge
    const challenge = data as any;
    const days = challenge.days.map((day: any) => {
      // מציאת המשימה הראשית שמכילה את הסרטון
      const mainVideoTask = day.tasks?.find((task: any) => task.is_main_video === true);
      
      // URL הסרטון נלקח מהמשימה הראשית אם קיימת, אחרת מרמת היום
      const effectiveVideoUrl = mainVideoTask?.video_url || day.video_url;
      
      return {
        id: day.id,
        dayNumber: day.day_number,
        videoId: day.video_id,
        videoUrl: effectiveVideoUrl,
        videoTitle: day.video_title,
        videoDescription: day.video_description,
        thumbnailUrl: day.thumbnail_url,
        tasks: day.tasks?.map((task: any) => ({
          id: task.id,
          text: task.text,
          status: task.status,
          completed: false,
          requiresVideoWatch: task.requires_video_watch,
          videoUrl: task.video_url,
          videoTitle: task.video_title,
          isMainVideo: task.is_main_video,
        })) || [],
        unlockDependencies: day.features?.reduce((acc: any, feature: any) => {
          acc[feature.feature_key] = feature.unlocked;
          return acc;
        }, {}) || {},
      };
    });

    return {
      id: challenge.id,
      title: challenge.title,
      description: challenge.description,
      status: challenge.status,
      startDate: challenge.start_date,
      endDate: challenge.end_date,
      totalDays: challenge.total_days,
      coverImage: challenge.cover_image,
      days,
      challengeType: challenge.challenge_type,
      featureUnlockDays: {},
      preparationDays: challenge.preparation_days || 2,
      paceMode: challenge.pace_mode || undefined,
      paceDays: challenge.pace_days || undefined,
      anchorDay: challenge.anchor_day ?? undefined,
      purchaseReference: challenge.purchase_reference || undefined,
      entryLine: challenge.entry_line ?? undefined,
      paidEntry: challenge.paid_entry ?? undefined,
      createdAt: challenge.created_at,
      updatedAt: challenge.updated_at,
    };
  },

  // קבלת אתגרים לפי סוג
  async getChallengesByType(type: ChallengeType): Promise<ChallengeListItem[]> {
    const { data, error } = await supabase
      .from("challenges")
      .select("*")
      .eq("challenge_type", type)
      .order("created_at", { ascending: false });

    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      status: item.status as "active" | "upcoming" | "completed",
      startDate: item.start_date,
      endDate: item.end_date,
      totalDays: item.total_days,
      coverImage: item.cover_image,
      participantsCount: item.participants_count,
      challengeType: item.challenge_type as ChallengeType,
    }));
  },

  // קבלת אתגרים לפי סטטוס
  async getChallengesByStatus(status: "active" | "upcoming" | "completed"): Promise<ChallengeListItem[]> {
    const { data, error } = await supabase
      .from("challenges")
      .select("*")
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      status: item.status as "active" | "upcoming" | "completed",
      startDate: item.start_date,
      endDate: item.end_date,
      totalDays: item.total_days,
      coverImage: item.cover_image,
      participantsCount: item.participants_count,
      challengeType: item.challenge_type as ChallengeType,
    }));
  },

  // קבלת אתגרים לפי סוג וסטטוס
  async getChallengesByTypeAndStatus(
    type: ChallengeType,
    status: "active" | "upcoming" | "completed"
  ): Promise<ChallengeListItem[]> {
    const { data, error } = await supabase
      .from("challenges")
      .select("*")
      .eq("challenge_type", type)
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      status: item.status as "active" | "upcoming" | "completed",
      startDate: item.start_date,
      endDate: item.end_date,
      totalDays: item.total_days,
      coverImage: item.cover_image,
      participantsCount: item.participants_count,
      challengeType: item.challenge_type as ChallengeType,
    }));
  },

  // שכפול אתגר עם כל התוכן
  async duplicateChallenge(
    sourceId: string,
    newData: { title: string; startDate: string; endDate: string; totalDays: number },
    copyDependencies: boolean = true
  ): Promise<any> {
    // 1. טעינת האתגר המקורי עם כל הימים, משימות ותלויות
    const source = await this.getChallengeById(sourceId);
    if (!source) throw new Error("אתגר מקור לא נמצא");

    // 2. שליפת קונפיגורציית מקור ישירות מה-DB (getChallengeById לא ממפה את כל השדות)
    const { data: sourceConfig, error: configError } = (await supabase
      .from("challenges")
      .select("pace_mode, pace_days, anchor_day, grace_period_days, purchase_reference, entry_line, paid_entry")
      .eq("id", sourceId)
      .single()) as { data: DuplicateSourceConfig | null; error: any };
    if (configError) throw configError;

    // 3. יצירת אתגר חדש
    const { data: newChallenge, error: createError } = await supabase
      .from("challenges")
      .insert(
        buildDuplicatePayload(sourceConfig!, newData, {
          description: source.description,
          challengeType: source.challengeType,
          coverImage: source.coverImage,
          preparationDays: source.preparationDays,
        }) as any
      )
      .select()
      .single();

    if (createError) throw createError;

    // 4. purchase_reference למחזור החדש — מעתיקים, לא מזיזים (מלבד קורסים).
    //
    // purchase_reference הוא הקישור היחיד בין מוצר ב-WooCommerce לאתגר. אם הוא אובד,
    // כל רכישה נכשלת ב-product_not_found והלקוחה משלמת ולא מקבלת כלום.
    //
    // הגרסה המקורית ניקתה קודם את המקור ואז כתבה ליעד — כישלון באמצע מחק את הערך
    // משתי השורות בלי דרך לשחזר (אירוע 2026-08-05, מוצר 13565: 13 משלמות, 8 מהן
    // בלי גישה 10 ימים; ומכיוון שהשכפול הבא ראה מקור ריק, זה לא תיקן את עצמו לעולם).
    //
    // התיקון האמיתי הוא לא לסדר את ההעברה אלא לבטל אותה: מחזור חדש *נולד* עם
    // התווית, ואין רגע שבו היא "בדרך" ויכולה ליפול. כותבים ליעד תמיד; מנקים את
    // המקור רק כשמדובר בקורס (ראה ההסבר על maybeSingle למטה).
    if (sourceConfig.purchase_reference) {
      const { error: setError } = await supabase
        .from("challenges")
        .update({ purchase_reference: sourceConfig.purchase_reference })
        .eq("id", newChallenge.id);
      if (setError) {
        // המקור עדיין מחזיק את הערך — ה-routing ממשיך לעבוד על המחזור הקודם.
        throw new Error(
          `purchase_reference copy to ${newChallenge.id} failed: ${setError.message}. ` +
            `Source ${sourceId} still holds '${sourceConfig.purchase_reference}' — routing is intact, ` +
            `but the new cohort will not receive purchases until this is set.`
        );
      }

      // קורס: שומרים על "שורה אחת לכל reference" ולכן מנקים את המקור.
      // זה לא גחמה — src/services/courseIdResolver.ts:33-35 ו-:65-67 מחפשים
      // קורס עם .eq('challenge_type','course').eq('purchase_reference', X)
      // .maybeSingle(), ו-maybeSingle *זורק* כששתי שורות חוזרות. שני קורסים
      // עם אותה תווית ישברו את פתיחת הקורסים.
      //
      // מחזור שבועי (entry/community): מעתיקים ולא מזיזים — כאן בדיוק אבדה
      // התווית ב-26.07. אף קורא עם maybeSingle לא מכוון לסוגים האלה, ו-
      // pickChallengeByCutoff נבנתה מלכתחילה כדי לבחור בין כמה מחזורים
      // שחולקים תווית. כך כל מחזור חדש *נולד* מתויג ואין מה לאבד בדרך.
      if (source.challengeType === "course") {
        const { error: clearError } = await supabase
          .from("challenges")
          .update({ purchase_reference: null })
          .eq("id", sourceId);
        if (clearError) {
          console.error(
            `[duplicateChallenge] purchase_reference clear from course ${sourceId} FAILED: ${clearError.message}. ` +
              `Both ${sourceId} and ${newChallenge.id} now hold '${sourceConfig.purchase_reference}' — ` +
              `two course rows sharing a reference WILL break courseIdResolver's maybeSingle(). Clear one manually.`
          );
        }
      }
    } else if (sourceConfig.paid_entry) {
      // החור השקט: אתגר בתשלום שכבר איבד את ה-reference משכפל את עצמו לנצח בלי אחד,
      // ואף רכישה לא תגיע אליו. אין מאיפה לשחזר את מזהה המוצר, אז לפחות מתריעים.
      console.error(
        `[duplicateChallenge] Source ${sourceId} is paid_entry but has NO purchase_reference — ` +
          `the new cohort ${newChallenge.id} cannot receive any purchase. ` +
          `Set purchase_reference to the WooCommerce product id on ${newChallenge.id} before it goes on sale.`
      );
    }

    // 5. העתקת כל הימים עם התוכן
    for (const day of source.days) {
      const { data: newDay, error: dayError } = await supabase
        .from("challenge_days")
        .insert({
          challenge_id: newChallenge.id,
          day_number: day.dayNumber,
          video_id: day.videoId,
          video_url: day.videoUrl || "",
          video_title: day.videoTitle || "",
          video_description: day.videoDescription || "",
          thumbnail_url: day.thumbnailUrl || null,
        })
        .select()
        .single();

      if (dayError) throw dayError;

      // 6. העתקת משימות
      if (day.tasks?.length) {
        const tasksToInsert = day.tasks.map((task, idx) => ({
          day_id: newDay.id,
          text: task.text,
          status: task.status as "צפייה" | "בוצע" | "צלם",
          requires_video_watch: task.requiresVideoWatch,
          video_url: task.videoUrl || null,
          video_title: task.videoTitle || null,
          is_main_video: task.isMainVideo || false,
          sort_order: idx,
        }));

        const { error: tasksError } = await supabase
          .from("challenge_day_tasks")
          .insert(tasksToInsert);

        if (tasksError) throw tasksError;
      }

      // 7. העתקת תלויות פיצ'רים
      if (copyDependencies && day.unlockDependencies) {
        const featuresToInsert = Object.entries(day.unlockDependencies)
          .filter(([, unlocked]) => unlocked)
          .map(([featureKey]) => ({
            day_id: newDay.id,
            feature_key: featureKey as any,
            unlocked: true,
          }));

        if (featuresToInsert.length) {
          const { error: featuresError } = await supabase
            .from("challenge_day_features")
            .insert(featuresToInsert);

          if (featuresError) throw featuresError;
        }
      }
    }

    return newChallenge;
  },

  // חיפוש אתגרים
  async searchChallenges(query: string): Promise<ChallengeListItem[]> {
    const { data, error } = await supabase
      .from("challenges")
      .select("*")
      .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
      .order("created_at", { ascending: false });

    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      status: item.status as "active" | "upcoming" | "completed",
      startDate: item.start_date,
      endDate: item.end_date,
      totalDays: item.total_days,
      coverImage: item.cover_image,
      participantsCount: item.participants_count,
      challengeType: item.challenge_type as ChallengeType,
    }));
  },
};
